/**
 * "Something is happening, new numbers are coming."
 *
 * Shown whenever the model is recalculating or a forecast is running. Both can
 * take long enough that a user reasonably concludes nothing happened: a
 * recalculate is a round trip through the whole engine, and a probabilistic
 * forecast runs for minutes.
 *
 * Drawn in CSS rather than shipped as a GIF. A GIF has fixed colours and a
 * fixed size, goes fuzzy on a high-density screen, cannot follow a theme, and
 * is another asset to load; three animated dots are a few lines and scale with
 * the font.
 *
 * `prefers-reduced-motion` stops the animation — the text still says what is
 * happening, so nothing is lost by holding still.
 */
export function WorkingIndicator({
  label,
  detail,
  className = '',
}: {
  label: string;
  /** What it is doing right now, when the caller knows. */
  detail?: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      role="status"
      aria-live="polite"
    >
      <span className="inline-flex items-end gap-[3px]" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="working-dot inline-block h-1.5 w-1.5 rounded-full bg-blue-600"
            style={{ animationDelay: `${i * 160}ms` }}
          />
        ))}
      </span>
      <span className="text-sm text-gray-600">
        {label}
        {detail && <span className="ml-1 text-gray-400">· {detail}</span>}
      </span>

      <style>{`
        @keyframes working-bounce {
          0%, 60%, 100% { transform: translateY(0); opacity: .45; }
          30%           { transform: translateY(-4px); opacity: 1; }
        }
        .working-dot { animation: working-bounce 1.1s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .working-dot { animation: none; opacity: .7; }
        }
      `}</style>
    </span>
  );
}
